import type {ComponentEnhancer} from 'recompose'
import {withProps, withHandlers, branch as branchHOC} from 'recompose'

import compose from 'lodash/flowRight'
import type {ConnectedProps} from 'react-redux'
import {connect} from 'react-redux'

import BranchSelect from '../components/common/BranchSelect/BranchSelect.container'
import {
  isForbiddenCombo,
  Modes,
} from '../components/pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeOverviewTab.modes'
import withEffect from '../hocs/withEffect'
import type {BranchProps} from '../hooks/routes'
import {withBranchProps, withProjectOrBuildTypeNode} from '../hooks/routes'
import type {State} from '../reducers/types'
import type {LocationProps} from '../routes'
import {getHrefWithQueryParams, withLocation} from '../routes'
import {getProjectId} from '../selectors'
import type {Branch} from '../services/rest'
import type {ProjectOrBuildTypeNode} from '../types'
import {BS} from '../types/BS_types'
import {parseBranch, stringifyBranch} from '../utils/branchNames'

import {Policy} from 'src/rest/branches'

type OwnProps = {
  className?: string
  projectOrBuildTypeNode?: ProjectOrBuildTypeNode | null | undefined
  policy?: Policy
  onSelect?: (selected: Branch | null | undefined, replace?: boolean) => unknown
}

const connector = connect((state: State, {projectOrBuildTypeNode}: OwnProps) => ({
  projectId: getProjectId(state, projectOrBuildTypeNode),
}))
type ReduxProps = ConnectedProps<typeof connector>

export default compose(
  withLocation,
  withBranchProps,
  branchHOC(
    (props: OwnProps) => props.projectOrBuildTypeNode == null,
    withProjectOrBuildTypeNode as ComponentEnhancer<OwnProps, OwnProps>,
  ),
  connector,
  withProps(({branch, policy, projectOrBuildTypeNode}: OwnProps & BranchProps) => {
    const isProjectPage = projectOrBuildTypeNode?.nodeType === 'project'
    const defaultPolicy = isProjectPage
      ? Policy.ACTIVE_HISTORY_AND_ACTIVE_VCS_BRANCHES
      : Policy.ALL_BRANCHES

    return {
      selected: branch,
      policy: policy ?? defaultPolicy,
      includeSubprojects: isProjectPage,
    }
  }),
  withEffect(
    (props: LocationProps & BranchProps & ReduxProps) => props.branch,
    props => props.projectId,
    props => props.location,
    props => props.navigate,
    (branch, projectId, location, navigate) => {
      if (branch != null || projectId == null) {
        return
      }

      const savedBranch = BS?.Branch?.getBranch(projectId)

      if (savedBranch != null) {
        navigate(
          getHrefWithQueryParams(location, null, ({mode, ...rest}) => ({
            ...rest,
            branch: savedBranch,
            mode: isForbiddenCombo(parseBranch(savedBranch), mode) ? Modes.BUILDS : mode,
          })),
          {
            replace: true,
          },
        )
      }
    },
  ),
  withHandlers({
    onSelect:
      ({navigate, location, projectId, onSelect}: OwnProps & LocationProps & ReduxProps) =>
      (selected: Branch | null | undefined, replace?: boolean) => {
        if (onSelect != null) {
          onSelect(selected, replace)
        } else {
          const branch = stringifyBranch(selected)

          if (projectId != null && BS?.Branch) {
            BS.Branch.saveBranch(projectId, branch)
          }

          navigate(
            getHrefWithQueryParams(location, null, ({mode, ...rest}) => ({
              ...rest,
              branch,
              mode: isForbiddenCombo(selected, mode) ? Modes.BUILDS : mode,
            })),
            {
              replace,
            },
          )
        }
      },
  }),
)(BranchSelect)
